import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Mail, Sparkles, Copy, Download, Eye, FileText,
  Building2, User, Target, BarChart3, Send, RefreshCw,
  Briefcase, TrendingUp, Heart, FileCheck, ChevronRight,
  FileSignature, Star, Plus, Edit3, Trash2, Save, X,
  Users, CopyPlus
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TEMPLATE_ICONS = {
  investor_update: TrendingUp,
  partnership_outreach: Briefcase,
  funding_announcement: Sparkles,
  corporate_introduction: Building2,
  strategic_partnership: Star,
  thank_you: Heart,
  quarterly_report: FileCheck
};

export default function EmailCampaignTab() {
  const [templates, setTemplates] = useState({});
  const [letters, setLetters] = useState([]);
  const [ceoInfo, setCeoInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generatedEmail, setGeneratedEmail] = useState(null);
  const [htmlPreview, setHtmlPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [editingLetter, setEditingLetter] = useState(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateRecipient, setDuplicateRecipient] = useState('');
  const [selectedLetterForDuplicate, setSelectedLetterForDuplicate] = useState(null);
  
  // Form state for AI generation
  const [formData, setFormData] = useState({
    template_type: 'strategic_partnership',
    recipient_name: '',
    recipient_company: '',
    subject_line: '',
    key_points: '',
    custom_prompt: '',
    include_metrics: false,
    metrics: {
      users: '',
      revenue: '',
      growth: ''
    }
  });

  // Form state for editing letter
  const [editForm, setEditForm] = useState({
    name: '',
    recipient_name: '',
    recipient_title: '',
    subject: '',
    body: ''
  });

  useEffect(() => {
    fetchTemplates();
    fetchLetters();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/email-campaign/templates`);
      setTemplates(response.data.templates);
      setCeoInfo(response.data.ceo_info);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchLetters = async () => {
    try {
      const response = await axios.get(`${API}/email-campaign/letters`);
      setLetters(response.data.letters || []);
    } catch (error) {
      console.error('Failed to fetch letters:', error);
    }
  };

  const loadLetter = async (letterId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/email-campaign/letters/${letterId}/html`);
      setGeneratedEmail({
        subject: response.data.subject,
        body: response.data.plain_text,
        preview_text: response.data.subject
      });
      setHtmlPreview({
        html: response.data.html
      });
      setShowPreview(true);
      toast.success('Letter loaded successfully!');
    } catch (error) {
      console.error('Failed to load letter:', error);
      toast.error('Failed to load letter');
    } finally {
      setLoading(false);
    }
  };

  const startEditingLetter = (letter) => {
    setEditForm({
      name: letter.name || '',
      recipient_name: letter.recipient_name || '',
      recipient_title: letter.recipient_title || '',
      subject: letter.subject || '',
      body: letter.body || ''
    });
    setEditingLetter(letter);
  };

  const saveLetterEdits = async () => {
    if (!editingLetter) return;
    setLoading(true);
    try {
      await axios.put(`${API}/email-campaign/letters/${editingLetter.letter_id}`, editForm);
      toast.success('Letter saved successfully!');
      setEditingLetter(null);
      fetchLetters();
    } catch (error) {
      console.error('Failed to save letter:', error);
      toast.error('Failed to save letter');
    } finally {
      setLoading(false);
    }
  };

  const openDuplicateModal = (letter) => {
    setSelectedLetterForDuplicate(letter);
    setDuplicateRecipient('');
    setShowDuplicateModal(true);
  };

  const duplicateLetter = async () => {
    if (!selectedLetterForDuplicate) return;
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/email-campaign/letters/${selectedLetterForDuplicate.letter_id}/duplicate`,
        null,
        { params: { new_recipient_name: duplicateRecipient || undefined } }
      );
      toast.success('Letter duplicated! You can now edit it for the new recipient.');
      setShowDuplicateModal(false);
      setDuplicateRecipient('');
      setSelectedLetterForDuplicate(null);
      fetchLetters();
      // Automatically start editing the new letter
      startEditingLetter(response.data.letter);
    } catch (error) {
      console.error('Failed to duplicate letter:', error);
      toast.error('Failed to duplicate letter');
    } finally {
      setLoading(false);
    }
  };

  const deleteLetter = async (letterId) => {
    if (!window.confirm('Are you sure you want to delete this letter?')) return;
    try {
      await axios.delete(`${API}/email-campaign/letters/${letterId}`);
      toast.success('Letter deleted');
      fetchLetters();
    } catch (error) {
      console.error('Failed to delete letter:', error);
      toast.error('Failed to delete letter');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleMetricChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      metrics: { ...prev.metrics, [field]: value }
    }));
  };

  const generateEmail = async () => {
    if (!formData.recipient_name) {
      toast.error('Please enter a recipient name');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        template_type: formData.template_type,
        recipient_name: formData.recipient_name,
        recipient_company: formData.recipient_company || null,
        subject_line: formData.subject_line || null,
        key_points: formData.key_points ? formData.key_points.split('\n').filter(p => p.trim()) : null,
        custom_prompt: formData.custom_prompt || null,
        include_metrics: formData.include_metrics,
        metrics: formData.include_metrics ? formData.metrics : null
      };

      const response = await axios.post(`${API}/email-campaign/generate`, payload);
      setGeneratedEmail(response.data);
      
      const htmlResponse = await axios.post(`${API}/email-campaign/preview-html`, payload);
      setHtmlPreview(htmlResponse.data);
      
      setShowPreview(true);
      toast.success('Email generated successfully!');
    } catch (error) {
      console.error('Failed to generate email:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate email');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const downloadHtml = () => {
    if (!htmlPreview?.html) return;
    const blob = new Blob([htmlPreview.html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `email-${formData.template_type}-${Date.now()}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('HTML downloaded!');
  };

  const selectedTemplate = templates[formData.template_type];

  // Render the editing modal
  if (editingLetter) {
    return (
      <div className="space-y-6">
        <Card className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-emerald-500/30">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Edit3 className="w-6 h-6 text-emerald-400" />
                <div>
                  <CardTitle className="text-xl">Editing Letter</CardTitle>
                  <CardDescription>Customize this letter for your recipient</CardDescription>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setEditingLetter(null)}
                  className="border-stone-600"
                >
                  <X className="w-4 h-4 mr-1" /> Cancel
                </Button>
                <Button
                  onClick={saveLetterEdits}
                  disabled={loading}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white"
                >
                  {loading ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  Save Changes
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Edit Form */}
          <div className="space-y-4">
            <Card className="bg-stone-900 border-stone-800">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileSignature className="w-5 h-5 text-amber-400" />
                  Letter Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-stone-400">Letter Name</Label>
                  <Input
                    value={editForm.name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Letter name for your reference"
                    className="bg-stone-800 border-stone-700 mt-1"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-stone-400">Recipient Name *</Label>
                    <Input
                      value={editForm.recipient_name}
                      onChange={(e) => setEditForm(prev => ({ ...prev, recipient_name: e.target.value }))}
                      placeholder="John Smith"
                      className="bg-stone-800 border-stone-700 mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-stone-400">Recipient Title</Label>
                    <Input
                      value={editForm.recipient_title}
                      onChange={(e) => setEditForm(prev => ({ ...prev, recipient_title: e.target.value }))}
                      placeholder="CEO, Company Name"
                      className="bg-stone-800 border-stone-700 mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-stone-400">Subject Line</Label>
                  <Input
                    value={editForm.subject}
                    onChange={(e) => setEditForm(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="Email subject..."
                    className="bg-stone-800 border-stone-700 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-stone-400">Letter Body</Label>
                  <Textarea
                    value={editForm.body}
                    onChange={(e) => setEditForm(prev => ({ ...prev, body: e.target.value }))}
                    placeholder="Write your letter content here..."
                    className="bg-stone-800 border-stone-700 mt-1 min-h-[400px] font-mono text-sm"
                  />
                  <p className="text-xs text-stone-500 mt-1">
                    Tip: Use blank lines to create paragraphs. Start lines with • for bullet points.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Live Preview */}
          <Card className="bg-stone-900 border-stone-800">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Eye className="w-5 h-5 text-amber-400" />
                Live Preview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-white rounded-lg p-6 text-black max-h-[600px] overflow-y-auto">
                <div className="border-b-2 border-amber-500 pb-4 mb-4">
                  <h2 className="text-amber-600 font-bold text-lg">GOnowbaby Media Group LLC</h2>
                  <p className="text-gray-500 text-xs">DISRUPTING TRAVEL & COMMERCE</p>
                </div>
                
                <div className="space-y-4">
                  <p className="text-gray-600 text-sm">
                    {editForm.recipient_name && (
                      <>
                        <strong>{editForm.recipient_name}</strong>
                        {editForm.recipient_title && <><br />{editForm.recipient_title}</>}
                      </>
                    )}
                  </p>
                  
                  {editForm.subject && (
                    <p className="font-semibold text-gray-800">RE: {editForm.subject}</p>
                  )}
                  
                  <div className="whitespace-pre-wrap text-gray-700 text-sm leading-relaxed">
                    {editForm.body.split('\n\n').map((para, i) => (
                      <p key={i} className="mb-3">
                        {para.startsWith('•') ? (
                          <ul className="list-disc ml-5">
                            {para.split('\n').map((line, j) => (
                              <li key={j}>{line.replace(/^•\s*/, '')}</li>
                            ))}
                          </ul>
                        ) : para}
                      </p>
                    ))}
                  </div>
                </div>
                
                <div className="border-t border-gray-200 pt-4 mt-6">
                  <p className="font-serif text-xl text-gray-800 italic">George Broughton</p>
                  <p className="text-gray-600 text-sm">Chief Executive Officer</p>
                  <p className="text-gray-500 text-sm">GOnowbaby Media Group LLC</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with CEO */}
      <Card className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/30">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-500/20 rounded-xl">
                <Mail className="w-8 h-8 text-amber-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Email Campaign Builder</h2>
                <p className="text-amber-300">AI-Powered Corporate Investment Communications</p>
              </div>
            </div>
            {ceoInfo && (
              <div className="flex items-center gap-3 bg-stone-900/50 rounded-xl p-3">
                <img 
                  src={ceoInfo.image_url} 
                  alt={ceoInfo.name}
                  className="w-14 h-14 rounded-full object-cover border-2 border-amber-500"
                />
                <div>
                  <p className="font-semibold text-white">{ceoInfo.name}</p>
                  <p className="text-xs text-stone-400">{ceoInfo.title}</p>
                  <p className="text-xs text-stone-500">{ceoInfo.company}</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Duplicate Modal */}
      {showDuplicateModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <Card className="bg-stone-900 border-stone-700 w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CopyPlus className="w-5 h-5 text-purple-400" />
                Duplicate Letter as Template
              </CardTitle>
              <CardDescription>
                Create a copy of this letter for a new investor
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-stone-400">New Recipient Name (optional)</Label>
                <Input
                  value={duplicateRecipient}
                  onChange={(e) => setDuplicateRecipient(e.target.value)}
                  placeholder="e.g., John Smith"
                  className="bg-stone-800 border-stone-700 mt-1"
                />
                <p className="text-xs text-stone-500 mt-1">
                  Leave blank to create a generic copy you can customize later
                </p>
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setShowDuplicateModal(false)}
                  className="border-stone-600"
                >
                  Cancel
                </Button>
                <Button
                  onClick={duplicateLetter}
                  disabled={loading}
                  className="bg-purple-500 hover:bg-purple-600 text-white"
                >
                  {loading ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <CopyPlus className="w-4 h-4 mr-1" />}
                  Duplicate & Edit
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Saved Letters Section */}
      {letters.length > 0 && !showPreview && (
        <Card className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-purple-500/30">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-purple-400" />
              Investor Letters
              <Badge className="ml-2 bg-purple-500/20 text-purple-400">{letters.length} Letters</Badge>
            </CardTitle>
            <CardDescription>Click Edit to customize, or Duplicate to create a version for a new investor</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3">
              {letters.map((letter) => (
                <div 
                  key={letter.letter_id}
                  className="p-4 bg-stone-900/80 rounded-lg border border-purple-500/20 hover:border-purple-500/50 transition-all"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-white flex items-center gap-2">
                        {letter.is_template && <Star className="w-4 h-4 text-amber-400" />}
                        {letter.name}
                      </h4>
                      <p className="text-sm text-stone-400 mt-1">
                        To: <span className="text-white">{letter.recipient_name}</span>
                        {letter.recipient_title && <span className="text-stone-500"> • {letter.recipient_title}</span>}
                      </p>
                      <p className="text-xs text-stone-500 mt-1 line-clamp-1">
                        RE: {letter.subject}
                      </p>
                      {letter.source_template_id && (
                        <Badge className="mt-2 bg-stone-800 text-stone-400 text-xs">
                          Duplicated from template
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => startEditingLetter(letter)}
                        className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                      >
                        <Edit3 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openDuplicateModal(letter)}
                        className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                        title="Duplicate for new investor"
                      >
                        <CopyPlus className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => loadLetter(letter.letter_id)}
                        disabled={loading}
                        className="bg-purple-500 hover:bg-purple-600 text-white"
                      >
                        {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      {!letter.is_template && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteLetter(letter.letter_id)}
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {!showPreview ? (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Template Selection */}
          <Card className="bg-stone-900 border-stone-800">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="w-5 h-5 text-amber-400" />
                AI Email Template
              </CardTitle>
              <CardDescription>Generate a new letter with AI</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {Object.entries(templates).map(([key, template]) => {
                const Icon = TEMPLATE_ICONS[key] || Mail;
                const isSelected = formData.template_type === key;
                return (
                  <button
                    key={key}
                    onClick={() => handleInputChange('template_type', key)}
                    className={`w-full p-3 rounded-lg border text-left transition-all ${
                      isSelected 
                        ? 'border-amber-500 bg-amber-500/10' 
                        : 'border-stone-700 hover:border-stone-600 bg-stone-800/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className={`w-4 h-4 ${isSelected ? 'text-amber-400' : 'text-stone-400'}`} />
                      <div>
                        <p className={`font-medium text-sm ${isSelected ? 'text-amber-400' : 'text-white'}`}>
                          {template.name}
                        </p>
                        <p className="text-xs text-stone-500 line-clamp-1">{template.description}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </CardContent>
          </Card>

          {/* Form */}
          <div className="lg:col-span-2 space-y-4">
            <Card className="bg-stone-900 border-stone-800">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5 text-amber-400" />
                  Recipient & Content
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-stone-400">Recipient Name *</Label>
                    <Input
                      value={formData.recipient_name}
                      onChange={(e) => handleInputChange('recipient_name', e.target.value)}
                      placeholder="John Smith"
                      className="bg-stone-800 border-stone-700 mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-stone-400">Company</Label>
                    <Input
                      value={formData.recipient_company}
                      onChange={(e) => handleInputChange('recipient_company', e.target.value)}
                      placeholder="Acme Ventures"
                      className="bg-stone-800 border-stone-700 mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-stone-400">Subject Line Hint</Label>
                  <Input
                    value={formData.subject_line}
                    onChange={(e) => handleInputChange('subject_line', e.target.value)}
                    placeholder="e.g., Q4 Growth Update, Partnership Opportunity..."
                    className="bg-stone-800 border-stone-700 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-stone-400">Key Points (one per line)</Label>
                  <Textarea
                    value={formData.key_points}
                    onChange={(e) => handleInputChange('key_points', e.target.value)}
                    placeholder="Strong Q4 performance&#10;New partnerships announced&#10;Expansion plans for 2026"
                    className="bg-stone-800 border-stone-700 mt-1 min-h-[80px]"
                  />
                </div>
                <div>
                  <Label className="text-stone-400">Additional Context</Label>
                  <Textarea
                    value={formData.custom_prompt}
                    onChange={(e) => handleInputChange('custom_prompt', e.target.value)}
                    placeholder="Any specific tone, details, or context..."
                    className="bg-stone-800 border-stone-700 mt-1"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-stone-900 border-stone-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <input
                    type="checkbox"
                    id="include-metrics"
                    checked={formData.include_metrics}
                    onChange={(e) => handleInputChange('include_metrics', e.target.checked)}
                    className="w-4 h-4 rounded border-stone-600 text-amber-500 focus:ring-amber-500"
                  />
                  <Label htmlFor="include-metrics" className="text-stone-300 cursor-pointer flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-amber-400" />
                    Include Business Metrics
                  </Label>
                </div>
                {formData.include_metrics && (
                  <div className="grid sm:grid-cols-3 gap-3">
                    <Input
                      value={formData.metrics.users}
                      onChange={(e) => handleMetricChange('users', e.target.value)}
                      placeholder="Active Users"
                      className="bg-stone-800 border-stone-700"
                    />
                    <Input
                      value={formData.metrics.revenue}
                      onChange={(e) => handleMetricChange('revenue', e.target.value)}
                      placeholder="Revenue/MRR"
                      className="bg-stone-800 border-stone-700"
                    />
                    <Input
                      value={formData.metrics.growth}
                      onChange={(e) => handleMetricChange('growth', e.target.value)}
                      placeholder="Growth Rate"
                      className="bg-stone-800 border-stone-700"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            <Button
              onClick={generateEmail}
              disabled={loading || !formData.recipient_name}
              className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-bold"
            >
              {loading ? (
                <><RefreshCw className="w-5 h-5 mr-2 animate-spin" /> Generating...</>
              ) : (
                <><Sparkles className="w-5 h-5 mr-2" /> Generate Email with AI</>
              )}
            </Button>
          </div>
        </div>
      ) : (
        /* Preview Section */
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() => setShowPreview(false)}
              className="border-stone-700"
            >
              ← Back to Editor
            </Button>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => copyToClipboard(generatedEmail?.body || '')}
                className="border-stone-700"
              >
                <Copy className="w-4 h-4 mr-1" /> Copy Text
              </Button>
              <Button
                variant="outline"
                onClick={() => copyToClipboard(htmlPreview?.html || '')}
                className="border-stone-700"
              >
                <Copy className="w-4 h-4 mr-1" /> Copy HTML
              </Button>
              <Button onClick={downloadHtml} className="bg-amber-500 hover:bg-amber-600 text-black">
                <Download className="w-4 h-4 mr-1" /> Download
              </Button>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            {/* Plain Text */}
            <Card className="bg-stone-900 border-stone-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileText className="w-4 h-4 text-amber-400" />
                  Plain Text
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="p-2 bg-stone-800 rounded">
                    <p className="text-xs text-stone-500">Subject</p>
                    <p className="text-white text-sm">{generatedEmail?.subject}</p>
                  </div>
                  <div className="p-3 bg-stone-800 rounded max-h-[350px] overflow-y-auto">
                    <pre className="text-stone-300 text-sm whitespace-pre-wrap font-sans">
                      {generatedEmail?.body}
                    </pre>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* HTML Preview */}
            <Card className="bg-stone-900 border-stone-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Eye className="w-4 h-4 text-amber-400" />
                  HTML Preview
                </CardTitle>
              </CardHeader>
              <CardContent>
                {htmlPreview?.html && (
                  <div className="bg-white rounded overflow-hidden">
                    <iframe
                      srcDoc={htmlPreview.html}
                      title="Email Preview"
                      className="w-full h-[350px] border-0"
                      sandbox="allow-same-origin"
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <Button
            onClick={() => {
              const newWindow = window.open('', '_blank');
              newWindow.document.write(htmlPreview?.html || '');
              newWindow.document.close();
            }}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-black font-bold"
          >
            <Send className="w-4 h-4 mr-2" /> Open Full Preview in New Tab
          </Button>
        </div>
      )}

      {/* CEO Signature Preview */}
      {ceoInfo && !showPreview && !editingLetter && (
        <Card className="bg-stone-900/50 border-stone-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-stone-400">CEO Signature (Auto-included)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-4 p-4 bg-stone-800 rounded-lg">
              <img 
                src={ceoInfo.image_url} 
                alt={ceoInfo.name}
                className="w-16 h-16 rounded-full object-cover border-2 border-amber-500"
              />
              <div>
                <p className="font-serif text-xl text-stone-300 italic">George Broughton</p>
                <p className="text-stone-400 text-sm">Founder & CEO</p>
                <p className="text-stone-500 text-sm">{ceoInfo.company}</p>
                <div className="flex gap-2 mt-1">
                  <span className="text-amber-400 text-xs">{ceoInfo.email}</span>
                  <span className="text-stone-600">|</span>
                  <span className="text-amber-400 text-xs">myhairchair.com</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
